/**
 * StreamSettler — reconciles a turn when it ends, INDEPENDENT of React.
 *
 * Settle cannot live in a mounted component. The actor and ChunkBuffer are
 * app-level singletons that survive chat switches, but a component is
 * key-remounted per chat, so a turn finishing while you looked at another chat
 * would never settle. Since the POST returns early and the answer keeps
 * streaming after it resolves, that is routine rather than an edge case — so
 * settle is driven by the machine reaching a terminal state.
 */

import { pickRawContextUsage } from 'src/content/ChatV2/lib/contextWindowUsage';
import {
  applyQueryPersisted,
  applyResponseId,
  metaSources,
  patchChatMeta,
  patchMessagesCache,
  upsertErrorResponse,
  upsertResponseContent,
  upsertStoppedResponse
} from 'src/content/ChatV2/lib/turnCacheWrites';
import {
  CHAT_STREAM_EVENT,
  CHAT_STREAM_STATE
} from 'src/content/ChatV2/machines/actions';
import type {
  ChatStreamActor,
  MachineRegistry
} from 'src/content/ChatV2/machines/machineRegistry';
import type { ChatTransport } from 'src/content/ChatV2/transport/ChatTransport';
import type { ChatId } from 'src/content/ChatV2/types/ids';
import { updateChatData } from 'src/redux/slices/chat';
import type { AppDispatch } from 'src/redux/store';

import type { ChatStreamRegistry } from './ChatStreamRegistry';

export interface StreamSettlerDeps {
  machineRegistry: MachineRegistry;
  streamRegistry: ChatStreamRegistry;
  transport: ChatTransport;
  dispatch: AppDispatch;
}

interface TrackedChat {
  unsubscribe: () => void;
  prevValue?: string;
  queryIdApplied: boolean;
  responseIdApplied: boolean;
  settled: boolean;
}

export class StreamSettler {
  private readonly tracked = new Map<ChatId, TrackedChat>();

  constructor(private readonly deps: StreamSettlerDeps) {}

  track(chatId: ChatId): void {
    if (this.tracked.has(chatId)) {
      return;
    }
    const actor = this.deps.machineRegistry.get(chatId);
    const entry: TrackedChat = {
      unsubscribe: () => {},
      queryIdApplied: false,
      responseIdApplied: false,
      settled: false
    };
    this.tracked.set(chatId, entry);
    const sub = actor.subscribe((snapshot) =>
      this.onSnapshot(chatId, actor, snapshot)
    );
    entry.unsubscribe = () => sub.unsubscribe();
  }

  untrack(chatId: ChatId): void {
    const entry = this.tracked.get(chatId);
    if (!entry) {
      return;
    }
    entry.unsubscribe();
    this.tracked.delete(chatId);
  }

  stopAll(): void {
    for (const entry of this.tracked.values()) {
      entry.unsubscribe();
    }
    this.tracked.clear();
  }

  private onSnapshot(
    chatId: ChatId,
    actor: ChatStreamActor,
    snapshot: ReturnType<ChatStreamActor['getSnapshot']>
  ): void {
    const entry = this.tracked.get(chatId);
    if (!entry) {
      return;
    }
    const value = snapshot.value as string;
    const ctx = snapshot.context;

    if (
      value === CHAT_STREAM_STATE.CONNECTING &&
      entry.prevValue !== CHAT_STREAM_STATE.CONNECTING
    ) {
      entry.queryIdApplied = false;
      entry.responseIdApplied = false;
      entry.settled = false;
      this.deps.streamRegistry.get(chatId).stepStore.reset();
    }

    if (
      !entry.queryIdApplied &&
      ctx.queryClientId != null &&
      ctx.queryServerId != null
    ) {
      entry.queryIdApplied = true;
      const { queryClientId, queryServerId } = ctx;
      this.deps.dispatch(
        patchMessagesCache(chatId, (rows) =>
          applyQueryPersisted(rows, queryClientId, queryServerId)
        )
      );
    }

    if (
      !entry.responseIdApplied &&
      entry.settled &&
      ctx.responseClientId != null &&
      ctx.responseServerId != null
    ) {
      entry.responseIdApplied = true;
      const { responseClientId, responseServerId, responseModelId } = ctx;
      this.deps.dispatch(
        patchMessagesCache(chatId, (rows) =>
          applyResponseId(
            rows,
            responseClientId,
            responseServerId,
            responseModelId
          )
        )
      );
    }

    if (value !== entry.prevValue) {
      if (value === CHAT_STREAM_STATE.DONE) {
        this.settle(chatId, ctx);
        this.settleSteps(chatId, ctx);
        this.settleContextUsage(chatId, ctx);
        entry.settled = true;
        if (ctx.responseServerId != null) {
          entry.responseIdApplied = true;
        }
        this.deps.transport.closeStream(chatId);
        actor.send({ type: CHAT_STREAM_EVENT.RESET });
      } else if (value === CHAT_STREAM_STATE.ABORTED) {
        this.settleStopped(chatId, ctx);
        entry.settled = true;
        if (ctx.responseServerId != null) {
          entry.responseIdApplied = true;
        }
        this.deps.streamRegistry.get(chatId).stepStore.cancel(undefined);
        this.deps.transport.closeStream(chatId);
        actor.send({ type: CHAT_STREAM_EVENT.RESET });
      } else if (value === CHAT_STREAM_STATE.ERROR) {
        this.fail(chatId, ctx);
        entry.settled = true;
        if (ctx.responseServerId != null) {
          entry.responseIdApplied = true;
        }
        this.deps.streamRegistry.get(chatId).stepStore.settle(undefined);
        this.deps.transport.closeStream(chatId);
        actor.send({ type: CHAT_STREAM_EVENT.RESET });
      }
    }

    entry.prevValue = value;
  }

  private settleStopped(
    chatId: ChatId,
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    const responseClientId = ctx.responseClientId;
    if (responseClientId == null) {
      return;
    }
    // Written even when nothing streamed yet (partialText === ''): the row is what
    // renders the "terminated by user" notice, and a reload shows that notice for the
    // backend's empty CANCELLED row, so the live turn must match it.
    const partialText = this.deps.streamRegistry
      .get(chatId)
      .buffer.getSnapshot();
    const serverId = ctx.responseServerId;
    this.deps.dispatch(
      patchMessagesCache(chatId, (rows) =>
        upsertStoppedResponse(
          rows,
          responseClientId,
          partialText,
          undefined,
          serverId
        )
      )
    );
  }

  private settleSteps(
    chatId: ChatId,
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    this.deps.streamRegistry
      .get(chatId)
      .stepStore.settle(ctx.meta?.additional_steps);
  }

  private fail(
    chatId: ChatId,
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    const { responseClientId, error } = ctx;
    if (responseClientId == null || error == null) {
      return;
    }
    const partial = this.deps.streamRegistry.get(chatId).buffer.getSnapshot();
    this.deps.dispatch(
      patchMessagesCache(chatId, (rows) =>
        upsertErrorResponse(
          rows,
          responseClientId,
          error,
          partial,
          ctx.responseServerId
        )
      )
    );
  }

  private settleContextUsage(
    chatId: ChatId,
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    const usage = pickRawContextUsage(ctx.meta);
    if (usage === undefined) {
      return;
    }
    this.deps.dispatch(
      patchChatMeta(chatId, { chatData: { context_usage: usage } })
    );
  }

  private settle(
    chatId: ChatId,
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    const responseClientId = ctx.responseClientId;
    if (responseClientId == null) {
      return;
    }
    const buffered = this.deps.streamRegistry.get(chatId).buffer.getSnapshot();
    const authoritative = ctx.meta?.response;
    const finalText =
      typeof authoritative === 'string' && authoritative !== ''
        ? authoritative
        : buffered;
    const files = metaSources(ctx.meta);
    const serverId = ctx.responseServerId;
    this.deps.dispatch(
      patchMessagesCache(chatId, (rows) =>
        upsertResponseContent(
          rows,
          responseClientId,
          finalText,
          files,
          serverId,
          ctx.meta?.additional_steps,
          ctx.responseModelId
        )
      )
    );

    this.settleChatSubject(ctx);
  }

  private settleChatSubject(
    ctx: ReturnType<ChatStreamActor['getSnapshot']>['context']
  ): void {
    const subject = ctx.meta?.chat_subject;
    if (!subject || !ctx.serverChatId) {
      return;
    }
    this.deps.dispatch(
      updateChatData({
        chatId: Number(ctx.serverChatId),
        chatData: { subject }
      })
    );
  }
}
